import React, { useEffect, useMemo, useState } from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Typography } from '@material-ui/core'

const number = (value) => Math.max(0, Number(value) || 0)
const closeEnough = (left, right) => Math.abs(number(left) - number(right)) < 0.00001
const scale = (value) => Number.isFinite(Number(value)) ? Math.min(6, Math.max(0, Number(value))) : 3
const step = (precision) => 1 / (10 ** scale(precision))
const validPrecision = (value, precision) => Math.abs(number(value) - Number(number(value).toFixed(scale(precision)))) < 0.0000005
const qtyText = (value, precision = 3) => Number(value || 0).toLocaleString('ru-RU', { maximumFractionDigits: scale(precision) })

const initialAllocations = (lines, preset = 'return') => (lines || []).reduce((result, line) => {
    const readyQty = number(line.ready_source?.qty)
    const recipeQty = number(line.recipe_source?.qty)
    return {
        ...result,
        [line.line_key]: {
            ready_return_qty: preset === 'discard' ? 0 : readyQty,
            ready_discard_qty: preset === 'discard' ? readyQty : 0,
            recipe_return_qty: preset === 'return' ? recipeQty : 0,
            recipe_finished_qty: preset === 'finished' ? recipeQty : 0,
            recipe_discard_qty: preset === 'discard' ? recipeQty : 0,
            component_returns: {},
        },
    }
}, {})

const WarehouseDispositionDialog = ({ state, onCancel, onConfirm }) => {
    const preview = state?.preview || {}
    const lines = preview.lines || []
    const [allocations, setAllocations] = useState({})
    const [reason, setReason] = useState('')

    useEffect(() => {
        setAllocations(initialAllocations(state?.preview?.lines || []))
        setReason('')
    }, [state])

    const setPreset = (preset) => setAllocations(initialAllocations(lines, preset))
    const updateAllocation = (line, field, value) => setAllocations((current) => {
        const previous = current[line.line_key] || initialAllocations([line])[line.line_key]
        const next = { ...previous, [field]: number(value) }
        const recipeQty = number(line.recipe_source?.qty)
        const remainingFraction = recipeQty > 0 ? Math.max(0, 1 - next.recipe_return_qty / recipeQty) : 0
        const componentReturns = { ...(next.component_returns || {}) }
        const components = line.components || []
        components.forEach((component) => {
            const maxReturn = number(component.qty) * remainingFraction
            if (number(componentReturns[component.item_id]) > maxReturn) componentReturns[component.item_id] = maxReturn
        })
        return { ...current, [line.line_key]: { ...next, component_returns: componentReturns } }
    })
    const updateComponentReturn = (line, component, value) => setAllocations((current) => {
        const previous = current[line.line_key] || initialAllocations([line])[line.line_key]
        return {
            ...current,
            [line.line_key]: {
                ...previous,
                component_returns: { ...(previous.component_returns || {}), [component.item_id]: number(value) },
            },
        }
    })

    const validation = useMemo(() => {
        let valid = lines.length > 0
        let hasDiscard = false
        lines.forEach((line) => {
            const row = allocations[line.line_key] || {}
            const lineScale = scale(line.precision_scale)
            const readyQty = number(line.ready_source?.qty)
            const recipeQty = number(line.recipe_source?.qty)
            const readyFields = [row.ready_return_qty, row.ready_discard_qty]
            const recipeFields = [row.recipe_return_qty, row.recipe_finished_qty, row.recipe_discard_qty]
            if (!closeEnough(readyFields.reduce((sum, value) => sum + number(value), 0), readyQty)) valid = false
            if (!closeEnough(recipeFields.reduce((sum, value) => sum + number(value), 0), recipeQty)) valid = false
            if (![...readyFields, ...recipeFields].every((value) => validPrecision(value, lineScale))) valid = false
            if (number(row.recipe_finished_qty) > 0 && !line.finished_available) valid = false
            if (number(row.ready_discard_qty) > 0 || number(row.recipe_discard_qty) > 0) hasDiscard = true
            const remainingFraction = recipeQty > 0 ? Math.max(0, 1 - number(row.recipe_return_qty) / recipeQty) : 0
            const components = line.components || []
            components.forEach((component) => {
                const returned = number(row.component_returns?.[component.item_id])
                if (returned > number(component.qty) * remainingFraction + 0.00001 || !validPrecision(returned, component.precision_scale)) valid = false
            })
        })
        if (hasDiscard && !reason.trim()) valid = false
        return { valid, hasDiscard }
    }, [allocations, lines, reason])

    const confirm = () => {
        const lineResults = lines.map((line) => {
            const row = allocations[line.line_key] || {}
            return {
                line_key: line.line_key,
                ready_return_qty: number(row.ready_return_qty),
                ready_discard_qty: number(row.ready_discard_qty),
                recipe_return_qty: number(row.recipe_return_qty),
                recipe_finished_qty: number(row.recipe_finished_qty),
                recipe_discard_qty: number(row.recipe_discard_qty),
                component_returns: (line.components || []).map((component) => ({
                    item_id: component.item_id,
                    qty: number(row.component_returns?.[component.item_id]),
                })).filter((component) => component.qty > 0),
            }
        })
        const hasComponentReturns = lineResults.some((line) => line.component_returns.length > 0)
        let action = 'mixed'
        if (!hasComponentReturns && lineResults.every((result) => {
            const line = lines.find((source) => source.line_key === result.line_key)
            return closeEnough(result.ready_return_qty, line?.ready_source?.qty) && closeEnough(result.ready_discard_qty, 0) && closeEnough(result.recipe_return_qty, line?.recipe_source?.qty) && closeEnough(result.recipe_finished_qty, 0) && closeEnough(result.recipe_discard_qty, 0)
        })) action = 'return'
        else if (!hasComponentReturns && lineResults.every((result) => {
            const line = lines.find((source) => source.line_key === result.line_key)
            return closeEnough(result.ready_return_qty, line?.ready_source?.qty) && closeEnough(result.ready_discard_qty, 0) && closeEnough(result.recipe_return_qty, 0) && closeEnough(result.recipe_finished_qty, line?.recipe_source?.qty) && closeEnough(result.recipe_discard_qty, 0)
        })) action = 'keep_finished'
        else if (!hasComponentReturns && lineResults.every((result) => {
            const line = lines.find((source) => source.line_key === result.line_key)
            return closeEnough(result.ready_return_qty, 0) && closeEnough(result.ready_discard_qty, line?.ready_source?.qty) && closeEnough(result.recipe_return_qty, 0) && closeEnough(result.recipe_finished_qty, 0) && closeEnough(result.recipe_discard_qty, line?.recipe_source?.qty)
        })) action = 'discard'
        onConfirm({ action, reason: reason.trim(), line_results: lineResults })
    }

    const allFinishedAvailable = lines.length > 0 && lines.every((line) => number(line.recipe_source?.qty) <= 0 || line.finished_available)

    return (
        <Dialog open={Boolean(state)} onClose={onCancel} maxWidth="md" fullWidth>
            <DialogTitle>Что произошло с позициями заказа?</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" color="textSecondary">
                    Распределите отдельно готовый остаток и количество, исполненное по рецептуре. Так штучный товар не будет дробиться между источниками.
                </Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, marginBottom: 14 }}>
                    <Button size="small" variant="outlined" onClick={() => setPreset('return')}>Всё вернуть</Button>
                    <Button size="small" variant="outlined" disabled={!allFinishedAvailable} onClick={() => setPreset('finished')}>Всё оставить готовым</Button>
                    <Button size="small" variant="outlined" onClick={() => setPreset('discard')}>Всё утилизировать</Button>
                </div>

                {lines.map((line) => {
                    const row = allocations[line.line_key] || {}
                    const lineScale = scale(line.precision_scale)
                    const inputStep = step(lineScale)
                    const readyQty = number(line.ready_source?.qty)
                    const recipeQty = number(line.recipe_source?.qty)
                    const readyAllocated = number(row.ready_return_qty) + number(row.ready_discard_qty)
                    const recipeAllocated = number(row.recipe_return_qty) + number(row.recipe_finished_qty) + number(row.recipe_discard_qty)
                    const validQty = closeEnough(readyAllocated, readyQty) && closeEnough(recipeAllocated, recipeQty)
                    const remainingFraction = recipeQty > 0 ? Math.max(0, 1 - number(row.recipe_return_qty) / recipeQty) : 0
                    const showComponents = number(row.recipe_finished_qty) + number(row.recipe_discard_qty) > 0 && (line.components || []).length > 0
                    return (
                        <div key={line.line_key} style={{ padding: 14, marginBottom: 10, border: `1px solid ${validQty ? '#e1e5eb' : '#d76a6a'}`, borderRadius: 8, background: '#fafbfc' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
                                <div>
                                    <Typography variant="body2" style={{ fontWeight: 600 }}>{line.product_name}</Typography>
                                    <Typography variant="caption" color="textSecondary">В заказе: {qtyText(line.qty, lineScale)} · учтено: {Number(line.consumed_cost || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽</Typography>
                                </div>
                                {!validQty && <Typography variant="caption" style={{ color: '#b33f3f', whiteSpace: 'nowrap' }}>Распределите источники полностью</Typography>}
                            </div>

                            {readyQty > 0 && (
                                <div style={{ padding: 11, marginBottom: 10, border: '1px solid #dce7f5', borderRadius: 7, background: '#f5f9fe' }}>
                                    <Typography variant="caption" style={{ display: 'block', fontWeight: 600, marginBottom: 7 }}>Из готового остатка: {qtyText(readyQty, lineScale)} {line.ready_source?.unit_name || ''}</Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" variant="outlined" type="number" label="Вернуть в готовый остаток" value={row.ready_return_qty ?? 0} onChange={(event) => updateAllocation(line, 'ready_return_qty', event.target.value)} inputProps={{ min: 0, max: readyQty, step: inputStep }} /></Grid>
                                        <Grid item xs={12} sm={6}><TextField fullWidth size="small" variant="outlined" type="number" label="Утилизировать" value={row.ready_discard_qty ?? 0} onChange={(event) => updateAllocation(line, 'ready_discard_qty', event.target.value)} inputProps={{ min: 0, max: readyQty, step: inputStep }} /></Grid>
                                    </Grid>
                                </div>
                            )}

                            {recipeQty > 0 && (
                                <div style={{ padding: 11, border: '1px solid #e6e1d7', borderRadius: 7, background: '#fdfbf7' }}>
                                    <Typography variant="caption" style={{ display: 'block', fontWeight: 600, marginBottom: 7 }}>По рецептуре: {qtyText(recipeQty, lineScale)} {line.recipe_source?.unit_name || ''}</Typography>
                                    <Grid container spacing={1}>
                                        <Grid item xs={12} sm={4}><TextField fullWidth size="small" variant="outlined" type="number" label="Не готовили" value={row.recipe_return_qty ?? 0} onChange={(event) => updateAllocation(line, 'recipe_return_qty', event.target.value)} inputProps={{ min: 0, max: recipeQty, step: inputStep }} /></Grid>
                                        <Grid item xs={12} sm={4}><TextField fullWidth size="small" variant="outlined" type="number" label="Готово и осталось" value={row.recipe_finished_qty ?? 0} disabled={!line.finished_available} onChange={(event) => updateAllocation(line, 'recipe_finished_qty', event.target.value)} inputProps={{ min: 0, max: recipeQty, step: inputStep }} helperText={!line.finished_available ? 'Нет готовой складской позиции' : line.finished_item_name || ''} /></Grid>
                                        <Grid item xs={12} sm={4}><TextField fullWidth size="small" variant="outlined" type="number" label="Утилизировано" value={row.recipe_discard_qty ?? 0} onChange={(event) => updateAllocation(line, 'recipe_discard_qty', event.target.value)} inputProps={{ min: 0, max: recipeQty, step: inputStep }} /></Grid>
                                    </Grid>
                                </div>
                            )}

                            {showComponents && (
                                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e2e5ea' }}>
                                    <Typography variant="caption" style={{ display: 'block', fontWeight: 600, marginBottom: 7 }}>Дополнительно вернуть пригодные компоненты рецептурной части</Typography>
                                    <Grid container spacing={1}>
                                        {(line.components || []).map((component) => {
                                            const componentScale = scale(component.precision_scale)
                                            const maxReturn = number(component.qty) * remainingFraction
                                            return <Grid item xs={12} sm={6} key={component.item_id}><TextField fullWidth size="small" variant="outlined" type="number" label={component.name || `Позиция #${component.item_id}`} value={row.component_returns?.[component.item_id] ?? 0} onChange={(event) => updateComponentReturn(line, component, event.target.value)} inputProps={{ min: 0, max: maxReturn, step: step(componentScale) }} helperText={`Можно вернуть до ${qtyText(maxReturn, componentScale)} ${component.unit_name || ''}`} /></Grid>
                                        })}
                                    </Grid>
                                </div>
                            )}
                        </div>
                    )
                })}

                <Typography variant="caption" color="textSecondary">
                    Учтённая стоимость заказа: {Number(preview.consumed_cost || 0).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ₽
                </Typography>
                {validation.hasDiscard && <TextField autoFocus fullWidth multiline rows={2} variant="outlined" size="small" label="Причина утилизации" value={reason} onChange={(event) => setReason(event.target.value)} style={{ marginTop: 14 }} />}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Отмена</Button>
                <Button color="primary" variant="contained" disabled={!validation.valid} onClick={confirm}>Продолжить</Button>
            </DialogActions>
        </Dialog>
    )
}

export default WarehouseDispositionDialog
